import { Box } from "lucide-react";

const cellLabel = {
  fontSize: 10,
  color: "var(--text3)",
  fontWeight: 600,
  textTransform: "uppercase",
};
const cellValue = { fontSize: 12, color: "var(--text)", marginTop: 1 };
const border = "1px solid var(--border2)";

export default function PfmeaSheetHeader({ doc, coreTeam = [] }) {
  const logoUrl = doc?.Company?.logo_url;
  const companyName = doc?.Company?.name;

  // Core Team rendered as a 3-column grid, matching the source Excel layout.
  const teamCols = 3;
  const teamRows = [];
  for (let i = 0; i < coreTeam.length; i += teamCols) {
    teamRows.push(coreTeam.slice(i, i + teamCols));
  }

  return (
    <div
      style={{ border, borderRadius: 8, overflow: "hidden", marginBottom: 20 }}
    >
      {/* Logo + Title row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          borderBottom: border,
          background: "var(--surface)",
        }}
      >
        <div
          style={{
            width: 130,
            padding: 10,
            borderRight: border,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={companyName || "Company logo"}
              style={{ maxWidth: "100%", maxHeight: 60, objectFit: "contain" }}
            />
          ) : (
            <div
              style={{
                width: 60,
                height: 60,
                borderRadius: 8,
                background: "var(--surface2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box size={22} color="var(--text3)" />
            </div>
          )}
        </div>
        <div style={{ flex: 1, textAlign: "center", padding: 10 }}>
          <div style={{ fontSize: 17, fontWeight: 800, letterSpacing: 0.3 }}>
            POTENTIAL FAILURE MODE AND EFFECT ANALYSIS (PROCESS FMEA)
          </div>
          {companyName && (
            <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 2 }}>
              {companyName}
            </div>
          )}
        </div>
      </div>

      {/* Item / Model / Core Team | Process Resp / Key Date / Part No | FMEA No / Date / Rev */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
        }}
      >
        {/* Column 1 — Item, Model, Core Team */}
        <div style={{ borderRight: border, padding: 10 }}>
          <div style={{ marginBottom: 8 }}>
            <div style={cellLabel}>Item</div>
            <div style={cellValue}>{doc?.item_name || "—"}</div>
          </div>
          <div style={{ marginBottom: 8 }}>
            <div style={cellLabel}>Model</div>
            <div style={cellValue}>{doc?.model || "—"}</div>
          </div>
          <div>
            <div style={{ ...cellLabel, marginBottom: 4 }}>Core Team</div>
            {teamRows.length === 0 ? (
              <div style={{ fontSize: 12, color: "var(--text3)" }}>—</div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <tbody>
                  {teamRows.map((row, i) => (
                    <tr key={i}>
                      {row.map((name, j) => (
                        <td
                          key={j}
                          style={{
                            fontSize: 11,
                            padding: "3px 6px",
                            border,
                          }}
                        >
                          {name}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Column 2 — Process Responsibility, Key Date, Part No */}
        <div style={{ borderRight: border, padding: 10 }}>
          <div style={{ marginBottom: 8 }}>
            <div style={cellLabel}>Process Responsibility</div>
            <div style={cellValue}>{doc?.process_responsibility || "—"}</div>
          </div>
          <div style={{ marginBottom: 8 }}>
            <div style={cellLabel}>Key Date</div>
            <div style={cellValue}>{doc?.key_date || "—"}</div>
          </div>
          <div>
            <div style={cellLabel}>Part No</div>
            <div style={cellValue}>
              {doc?.Product?.name}{" "}
              {doc?.Product?.code ? `(${doc.Product.code})` : ""}
            </div>
          </div>
        </div>

        {/* Column 3 — FMEA No, FMEA Date, Rev No/Date */}
        <div style={{ padding: 10 }}>
          <div style={{ marginBottom: 8 }}>
            <div style={cellLabel}>FMEA No</div>
            <div style={cellValue}>{doc?.fmea_no || "—"}</div>
          </div>
          <div style={{ marginBottom: 8 }}>
            <div style={cellLabel}>FMEA Date (Orig)</div>
            <div style={cellValue}>{doc?.fmea_date || "—"}</div>
          </div>
          <div>
            <div style={cellLabel}>FMEA (Rev/Date)</div>
            <div style={cellValue}>
              {doc?.rev_no || "—"} {doc?.rev_date ? `/ ${doc.rev_date}` : ""}
            </div>
          </div>
        </div>
      </div>

      {doc?.rev_description && (
        <div
          style={{
            padding: "8px 10px",
            borderTop: border,
            fontSize: 11,
            color: "var(--text2)",
          }}
        >
          <b>Rev Description:</b> {doc.rev_description}
        </div>
      )}

      {/* Fixed legend line, matches the source Excel exactly */}
      <div
        style={{
          padding: "6px 10px",
          borderTop: border,
          background: "var(--surface2)",
          fontSize: 10,
          color: "var(--text3)",
        }}
      >
        N.O: Next Operation, S.O: Subsequent Operation, C.E: Customer, E.U: End
        User effect, O.S: Operator Safety, M.C: Machine/Tool
      </div>
    </div>
  );
}
